import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import GameWrapper from '@/pages/jeux/components/GameWrapper';
import GameOverModal from '@/pages/jeux/components/GameOverModal';
import { useGame } from '@/contexts/GameContext';
import { HEALTH_TIPS } from '@/mocks/jeux-data';

type Level = 1 | 2 | 3 | 4 | 5;

interface MazeLevel {
  name: string;
  grid: number[][];
  start: [number, number];
  end: [number, number];
}

function generateMaze(level: Level): MazeLevel {
  const sizes: Record<Level, number> = { 1: 8, 2: 10, 3: 12, 4: 15, 5: 18 };
  const size = sizes[level];
  const grid: number[][] = Array(size).fill(null).map(() => Array(size).fill(1));
  const start: [number, number] = [1, 0];
  const end: [number, number] = [size - 2, size - 1];

  function carve(x: number, y: number) {
    grid[y][x] = 0;
    const dirs = [[0, -2], [2, 0], [0, 2], [-2, 0]].sort(() => Math.random() - 0.5);
    for (const [dx, dy] of dirs) {
      const nx = x + dx;
      const ny = y + dy;
      if (nx > 0 && nx < size - 1 && ny > 0 && ny < size - 1 && grid[ny][nx] === 1) {
        grid[y + dy / 2][x + dx / 2] = 0;
        carve(nx, ny);
      }
    }
  }

  carve(1, 1);
  grid[start[1]][start[0]] = 0;
  grid[end[1]][end[0]] = 0;

  return { name: `Niveau ${level}`, grid, start, end };
}

export default function LabyrintheGame() {
  const navigate = useNavigate();
  const { startGame, endGame, gameStatus, gameScore, gameTime, gameErrors, updateScore, updateErrors } = useGame();
  const [level, setLevel] = useState<Level | null>(null);
  const [maze, setMaze] = useState<MazeLevel | null>(null);
  const [playerPos, setPlayerPos] = useState<[number, number]>([1, 0]);
  const [showGameOver, setShowGameOver] = useState(false);
  const [started, setStarted] = useState(false);
  const [completedLevels, setCompletedLevels] = useState<number>(0);

  const handleStart = (l: Level) => {
    setLevel(l);
    const m = generateMaze(l);
    setMaze(m);
    setPlayerPos(m.start);
    setStarted(true);
    setShowGameOver(false);
    startGame('labyrinthe');
  };

  const movePlayer = useCallback((dx: number, dy: number) => {
    if (!maze || !started || gameStatus !== 'playing') return;
    const [x, y] = playerPos;
    const nx = x + dx;
    const ny = y + dy;

    if (ny >= 0 && ny < maze.grid.length && nx >= 0 && nx < maze.grid[0].length && maze.grid[ny][nx] === 0) {
      setPlayerPos([nx, ny]);

      if (nx === maze.end[0] && ny === maze.end[1]) {
        const points = (level || 1) * 100;
        updateScore(gameScore + points);
        const newCompleted = completedLevels + 1;
        setCompletedLevels(newCompleted);

        if (level && level < 5) {
          const nextLevel = (level + 1) as Level;
          setLevel(nextLevel);
          const m = generateMaze(nextLevel);
          setMaze(m);
          setPlayerPos(m.start);
        } else {
          endGame(gameScore + points, gameTime, gameErrors);
          setShowGameOver(true);
        }
      }
    }
  }, [maze, playerPos, started, gameStatus, level, completedLevels, gameScore, gameTime, gameErrors, updateScore, endGame]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowUp' || e.key === 'z') { e.preventDefault(); movePlayer(0, -1); }
      if (e.key === 'ArrowDown' || e.key === 's') { e.preventDefault(); movePlayer(0, 1); }
      if (e.key === 'ArrowLeft' || e.key === 'q') { e.preventDefault(); movePlayer(-1, 0); }
      if (e.key === 'ArrowRight' || e.key === 'd') { e.preventDefault(); movePlayer(1, 0); }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [movePlayer]);

  const handleReplay = () => { if (level) handleStart(1 as Level); };
  const handleQuit = () => { endGame(gameScore, gameTime, gameErrors); navigate('/jeux', { replace: true }); };

  if (!started) {
    return (
      <GameWrapper gameName="Labyrinthe Santé" onQuit={handleQuit} showPause={false}>
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="bg-background-50 rounded-3xl p-8 max-w-md w-full text-center border border-background-200/70">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-road-map-line text-3xl text-green-600"></i>
            </div>
            <h2 className="text-xl font-bold text-foreground-950 mb-2">Labyrinthe Santé</h2>
            <p className="text-foreground-600 text-sm mb-4">Aide l&apos;ambulance à atteindre l&apos;hôpital !</p>
            <p className="text-xs text-foreground-500 mb-6">Utilise les flèches ou ZQSD pour te déplacer</p>
            <button onClick={() => handleStart(1)} className="w-full py-3 bg-primary-500 text-background-50 rounded-xl font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-play-fill mr-2"></i> Commencer
            </button>
          </div>
        </div>
      </GameWrapper>
    );
  }

  if (!maze) return null;

  const cellSize = Math.min(32, Math.floor(500 / maze.grid.length));

  return (
    <GameWrapper gameName="Labyrinthe Santé" onQuit={handleQuit}>
      <div className="flex-1 flex flex-col items-center justify-center p-4">
        <div className="text-sm text-foreground-600 mb-3">Niveau {level}/5</div>
        <div
          className="border-2 border-foreground-300 rounded-xl overflow-hidden bg-background-50"
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${maze.grid[0].length}, ${cellSize}px)`,
            gridTemplateRows: `repeat(${maze.grid.length}, ${cellSize}px)`,
          }}
        >
          {maze.grid.flatMap((row, y) =>
            row.map((cell, x) => {
              const isPlayer = x === playerPos[0] && y === playerPos[1];
              const isStart = x === maze.start[0] && y === maze.start[1];
              const isEnd = x === maze.end[0] && y === maze.end[1];

              return (
                <div
                  key={`${x}-${y}`}
                  className="flex items-center justify-center"
                  style={{
                    backgroundColor: cell === 1 ? '#374151' : 'transparent',
                  }}
                >
                  {cell === 0 && isPlayer && (
                    <i className="ri-taxi-line text-lg text-red-500"></i>
                  )}
                  {cell === 0 && isEnd && !isPlayer && (
                    <i className="ri-hospital-line text-lg text-green-500"></i>
                  )}
                  {cell === 0 && isStart && !isPlayer && (
                    <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Mobile controls */}
        <div className="mt-4 grid grid-cols-3 gap-2 md:hidden">
          <div /><button onTouchStart={() => movePlayer(0, -1)} className="w-12 h-12 bg-background-100 rounded-xl flex items-center justify-center cursor-pointer"><i className="ri-arrow-up-line"></i></button><div />
          <button onTouchStart={() => movePlayer(-1, 0)} className="w-12 h-12 bg-background-100 rounded-xl flex items-center justify-center cursor-pointer"><i className="ri-arrow-left-line"></i></button>
          <div className="w-12 h-12" />
          <button onTouchStart={() => movePlayer(1, 0)} className="w-12 h-12 bg-background-100 rounded-xl flex items-center justify-center cursor-pointer"><i className="ri-arrow-right-line"></i></button>
          <div /><button onTouchStart={() => movePlayer(0, 1)} className="w-12 h-12 bg-background-100 rounded-xl flex items-center justify-center cursor-pointer"><i className="ri-arrow-down-line"></i></button><div />
        </div>
      </div>

      {showGameOver && (
        <GameOverModal
          score={gameScore}
          timeElapsed={gameTime}
          errors={gameErrors}
          gameName="labyrinthe"
          message="Tous les niveaux terminés !"
          onReplay={handleReplay}
          onQuit={handleQuit}
          healthTip={HEALTH_TIPS[Math.floor(Math.random() * HEALTH_TIPS.length)]}
        />
      )}
    </GameWrapper>
  );
}